import React, { useEffect, useState, useRef, useMemo } from 'react';
import './TerminalLoader.scss';
import { useAgentId, AgentIdInfo } from './utils/pid';

// 乱码字符集与工具（模块级，供解码组件与终端共用）
const glitchChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*+<>=/?\\|{}[]~';

function randomGlitch(len: number) {
  let s = '';
  for (let i = 0; i < len; i++) s += glitchChars[Math.floor(Math.random() * glitchChars.length)];
  return s;
}

function glitchDelay(min: number, max: number) {
  return new Promise(resolve => setTimeout(resolve, Math.random() * (max - min) + min));
}

// Agent ID 解码显示（所有 pid 均生效）：
// 先显示乱码，再以终端打字机的解码节奏逐位解析为真实 ID（█ 光标随行）
// 数字 pid 等待映射表期间乱码持续翻动；无映射则永远维持乱码
export function AgentIdText({ raw, resolved }: AgentIdInfo & { raw: string }) {
  const isNumeric = /^\d+$/.test(raw);
  const target = isNumeric ? resolved : raw;
  const [text, setText] = useState(() => randomGlitch(6));
  const shuffleStart = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;

    async function run() {
      if (shuffleStart.current === null) shuffleStart.current = Date.now();
      // 乱码持续翻动：所有 pid 至少翻动 2s；数字 pid 在映射返回前一直翻动
      while (alive && (Date.now() - shuffleStart.current < 2000 || !target)) {
        setText(randomGlitch(6));
        await glitchDelay(90, 160);
      }
      if (!alive || !target) return; // 无映射：维持乱码

      // 解码动效：乱码 → 逐位解析（节奏同终端打字机：14-35ms/字符）
      const scrambled = randomGlitch(target.length);
      setText(scrambled);
      await glitchDelay(100, 200);
      for (let c = 0; c <= target.length; c++) {
        if (!alive) return;
        const resolvedPart = target.slice(0, c);
        const cursor = c < target.length ? '█' : '';
        const rest = scrambled.slice(c + (cursor ? 1 : 0));
        setText(resolvedPart + cursor + rest);
        await glitchDelay(14, 35);
      }
      if (alive) setText(target);
    }

    run();
    return () => { alive = false; };
  }, [isNumeric, raw, target]);

  return <>{text}</>;
}

// 定义每一行的数据结构
type LineData = {
  id: string;
  type: 'text' | 'progress';
  text?: string;
  progress?: number;
};

interface TerminalLoaderProps {
  onComplete?: () => void;
  lang: 'zh' | 'en' | 'jp';
  setLang: (lang: 'zh' | 'en' | 'jp') => void;
}

export default function TerminalLoader({ onComplete, lang, setLang }: TerminalLoaderProps) {
  const [initialized, setInitialized] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [lines, setLines] = useState<LineData[]>([]);
  const [canProceed, setCanProceed] = useState(false);
  const agent = useAgentId();
  
  const [isMobile, setIsMobile] = useState(false);

  // 💡 动态隐藏/显示 3D 标签的终极安全钩子
  useEffect(() => {
    const styleId = 'hide-labels-style';
    let el = document.getElementById(styleId);
    
    if (fadeOut) {
      if (el) el.remove();
      return;
    }
    
    if (!el) {
      el = document.createElement('style');
      el.id = styleId;
      el.innerHTML = `
        .place-label, .arc-label {
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(el);
    }

    return () => {
      const cleanupEl = document.getElementById(styleId);
      if (cleanupEl) cleanupEl.remove();
    };
  }, [fadeOut]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isSkipped = useRef(false);
  const decryptAudio = useRef<HTMLAudioElement | null>(null);
  const enterAudio = useRef<HTMLAudioElement | null>(null);
  const skipAudio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    decryptAudio.current = new Audio('/sound/decrpt.wav');
    decryptAudio.current.loop = true;
    enterAudio.current = new Audio('/sound/enter.wav');
    skipAudio.current = new Audio('/sound/skip.wav');
  }, []);

  const content = useMemo(() => ({
    en: {
      agentLine: 'Authentication Complete...',
      agentIdPrefix: 'Agent',
      initBtn: '> CLICK TO INITIATE SYSTEM_',
      bypassBtn: '> EXEC_OVERRIDE_ [-bypass]',
      skipBtn: 'SKIP >',
      enterBtn: 'ENTER',
      langLabel: 'Language',
      lines: [
        '> TERMINAL ACTIVE.',
        '> ESTABLISHING SECURE UPLINK...',
        '> AUTHENTICATING AGENT CREDENTIALS...',
        '> DECRYPTING XM ANOMALY DATA...',
        '> SYNTHESIZING GEOSPATIAL TOPOGRAPHY...',
        'progress',
        '> INITIALIZING "LINK2INF" PROTOCOL...',
        '> ALIGNING VECTORS: YUMEN-UNALASKA...',
        'progress',
        '> ...  ',
        '> ...  ',
        '> ...  ',
        '> ACCESS GRANTED.'
      ]
    },
    zh: {
      agentLine: '已确认特工身份...',
      agentIdPrefix: '特工',
      initBtn: '> 点击以初始化系统_',
      bypassBtn: '> 执行覆写_ [-bypass]',
      skipBtn: '跳过 >',
      enterBtn: '进入系统',
      langLabel: '语言',
      lines: [
        '> 终端已激活。',
        '> 正在建立安全上行链路...',
        '> 正在验证特工凭证...',
        '> 正在解密 XM 异常数据...',
        '> 正在合成地理空间拓扑...',
        'progress',
        '> 正在初始化 "LINK2INF" 协议...',
        '> 正在校准矢量：玉门 - 乌纳拉斯卡...',
        'progress',
        '> ...  ',
        '> ...  ',
        '> ...  ',
        '> 访问授权通过。'
      ]
    },
    jp: {
      agentLine: 'エージェント認証完了...',
      agentIdPrefix: 'エージェント',
      initBtn: '> クリックしてシステムを初期化_',
      bypassBtn: '> オーバーライド実行_ [-bypass]',
      skipBtn: 'スキップ >',
      enterBtn: 'エンター',
      langLabel: '言語',
      lines: [
        '> 端末アクティブ。',
        '> セキュアアップリンクを確立中...',
        '> エージェント資格情報を認証中...',
        '> XMアノマリーデータを復号中...',
        '> 地理空間トポロジーを合成中...',
        'progress',
        '> "LINK2INF" プロトコルを初期化中...',
        '> ベクトルを調整中：玉門 - ウナラスカ...',
        'progress',
        '> ...  ',
        '> ...  ',
        '> ...  ',
        '> アクセス許可。'
      ]
    }
  }), []);

  function delayRandom(min: number, max: number) {
    return new Promise(resolve => setTimeout(resolve, Math.random() * (max - min) + min));
  }

  function animateProgress(id: string, target: number, minMs: number, maxMs: number, cancelled: boolean) {
    return new Promise(resolve => {
      const duration = Math.random() * (maxMs - minMs) + minMs;
      const start = Date.now();
      function step() {
        if (cancelled || isSkipped.current) return resolve(undefined);
        const elapsed = Date.now() - start;
        const percent = Math.min(1, elapsed / duration);
        const currentVal = Math.round(percent * target);
        setLines(prev => prev.map(l => (l.id === id ? { ...l, progress: currentVal } : l)));
        if (percent < 1) {
          setTimeout(step, 20 + Math.random() * 25);
        } else {
          setLines(prev => prev.map(l => (l.id === id ? { ...l, progress: target } : l)));
          resolve(undefined);
        }
      }
      step();
    });
  }

  useEffect(() => {
    // 💡 核心修复：如果终端已经进入（cleared）或淡出（fadeOut），说明使命已完成，绝对拦截后台动画重启！
    if (!initialized || cleared || fadeOut) return;
    
    let cancelled = false;
    async function showLines() {
      const sequence = content[lang].lines.map((line) => {
        if (line === 'progress') return { type: 'progress' as const };
        return { type: 'text' as const, text: line };
      });
      for (let i = 0; i < sequence.length; i++) {
        if (cancelled || isSkipped.current) break;
        const step = sequence[i];
        const id = `line-${i}`;
        if (step.type === 'text' && step.text) {
          const fullText = step.text;
          const prefix = '> ';
          const actualText = fullText.slice(prefix.length);
          const words = actualText.split(' ');
          setLines(prev => [...prev, { id, type: 'text', text: prefix }]);
          const scrambledWords = words.map(w => {
            let res = '';
            for(let k = 0; k < w.length; k++) res += glitchChars[Math.floor(Math.random() * glitchChars.length)];
            return res;
          });
          const currentDisplayWords: string[] = [];
          for (let wIndex = 0; wIndex < words.length; wIndex++) {
            if (cancelled || isSkipped.current) break;
            currentDisplayWords.push(scrambledWords[wIndex]);
            setLines(prev => prev.map(l => l.id === id ? { ...l, text: prefix + currentDisplayWords.join(' ') } : l));
            await delayRandom(3, 10); 
          }
          if (cancelled || isSkipped.current) break;
          await delayRandom(100, 200); 
          if (decryptAudio.current && !isSkipped.current) {
            decryptAudio.current.currentTime = 0;
            decryptAudio.current.play().catch(() => {});
          }
          for (let c = 0; c <= actualText.length; c++) {
            if (cancelled || isSkipped.current) break;
            const resolved = actualText.slice(0, c);
            const cursor = c < actualText.length ? '█' : '';
            const remaining = scrambledWords.join(' ').slice(c + (cursor ? 1 : 0));
            setLines(prev => prev.map(l => l.id === id ? { ...l, text: prefix + resolved + cursor + remaining } : l));
            await delayRandom(14, 35); 
          }
          if (decryptAudio.current) decryptAudio.current.pause();
          if (!isSkipped.current) {
             setLines(prev => prev.map(l => l.id === id ? { ...l, text: fullText } : l));
             await delayRandom(150, 300); 
          }
        } else if (step.type === 'progress') {
          setLines(prev => [...prev, { id, type: 'progress', progress: 0 }]);
          if (decryptAudio.current && !isSkipped.current) {
            decryptAudio.current.currentTime = 0;
            decryptAudio.current.play().catch(() => {});
          }
          await animateProgress(id, 100, 480, 1200, cancelled); 
          if (decryptAudio.current) decryptAudio.current.pause();
          if (!isSkipped.current) await delayRandom(150, 300); 
        }
      }
      if (!cancelled && !isSkipped.current) setCanProceed(true);
    }
    showLines();
    return () => {
      cancelled = true;
      if (decryptAudio.current) decryptAudio.current.pause();
    };
    // 💡 必须把 cleared 和 fadeOut 加到依赖数组里，让 React 知道状态变化时该停止了
  }, [initialized, lang, content, cleared, fadeOut]); 

  const handleEnter = async () => {
    if (!canProceed) return;
    if (enterAudio.current) {
      enterAudio.current.currentTime = 0;
      enterAudio.current.play().catch(() => {});
    }
    setCleared(true); 
    const scrollLines = 25; 
    for (let i = 0; i < scrollLines; i++) {
      await delayRandom(15, 30); 
      setLines(prev => {
        const newLines = [...prev];
        if (newLines.length > 0) newLines.shift(); 
        newLines.push({ id: `clear-line-${i}`, type: 'text', text: ' ' });
        return newLines;
      });
    }
    await delayRandom(300, 600); 
    setFadeOut(true); 
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    isSkipped.current = true;
    if (decryptAudio.current) decryptAudio.current.pause();
    if (skipAudio.current) {
      skipAudio.current.currentTime = 0;
      skipAudio.current.play().catch(() => {});
    }
    setFadeOut(true);
    if (onComplete) onComplete(); 
  };

  if (!initialized) {
    return (
      <div className="terminal-loader">
        <div className="terminal-lang-switcher" style={{
          position: 'absolute',
          right: isMobile ? '16px' : '24px',
          top: isMobile ? '16px' : '24px',
          fontSize: '15px',
          color: 'rgba(210, 225, 245, 0.8)',
          display: 'flex',
          gap: '2px',
          alignItems: 'center',
          fontFamily: 'Manolo Mono, "Helvetica Neue", monospace',
          pointerEvents: 'auto',
          zIndex: 100
        }}>
          <span>{content[lang].langLabel}:</span>
          <button className={`ui-lang ${lang === 'zh' ? 'active' : ''}`} onClick={() => setLang('zh')}>中</button><span>|</span>
          <button className={`ui-lang ${lang === 'en' ? 'active' : ''}`} onClick={() => setLang('en')}>EN</button><span>|</span>
          <button className={`ui-lang ${lang === 'jp' ? 'active' : ''}`} onClick={() => setLang('jp')}>JP</button>
        </div>

        <div className="terminal-content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', top: '0', transform: 'translateX(-50%)', gap: '16px' }}>
          {agent.raw && (
            <div className="terminal-agent-row">
              <span className="terminal-prefix">{'>'}</span>
              {' ' + content[lang].agentLine}
            </div>
          )}
          {agent.raw && (
            <div className="terminal-agent-row">
              <span className="terminal-prefix">{'>'}</span>
              {' ' + content[lang].agentIdPrefix + ' [ '}
              <AgentIdText raw={agent.raw} resolved={agent.resolved} ready={agent.ready} />
              {' ]'}
            </div>
          )}
          <span
            className="enter-btn"
            style={{ marginTop: 0, fontSize: '15px', letterSpacing: '1px', alignSelf: 'flex-start', textDecoration: 'none', color: '#78aaff', cursor: 'pointer' }}
            onClick={() => {
              if (decryptAudio.current) { decryptAudio.current.play().catch(()=>{}); decryptAudio.current.pause(); }
              if (enterAudio.current) { enterAudio.current.play().catch(()=>{}); enterAudio.current.pause(); }
              setInitialized(true);
            }}
          >
            <span className="terminal-prefix">{'>'}</span>
            {' ' + content[lang].initBtn.substring(2)}
            <span className="blinking-cursor"></span>
          </span>
          <span
            className="enter-btn"
            style={{ marginTop: 0, fontSize: '14px', letterSpacing: '1px', opacity: 0.5, alignSelf: 'flex-start', textDecoration: 'none', cursor: 'pointer' }}
            onClick={() => {
              isSkipped.current = true;
              setInitialized(true);
              setFadeOut(true);
              if (onComplete) onComplete();
            }}
          >
            <span className="terminal-prefix">{'>'}</span>
            {' ' + content[lang].bypassBtn.substring(2)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`terminal-loader${fadeOut ? ' fade-out' : ''}`}>
      {!cleared && !canProceed && (
         <span className="terminal-skip-btn" onClick={handleSkip}>
           {content[lang].skipBtn.slice(0, -1)}<span className="terminal-prefix">{'>'}</span>
         </span>
      )}
      <div className="terminal-content">
        {lines.map(line => (
          <p key={line.id}>
            {line.type === 'text' && line.text ? (
              line.text.startsWith('> ') ? (
                <>
                  <span className="terminal-prefix">{'>'}</span>
                  {' ' + line.text.substring(2)}
                </>
              ) : line.text
            ) : ''}
            
            {line.type === 'progress' && line.progress !== undefined ? (
              <>
                <span className="terminal-prefix">{'>'}</span>
                {` [${'█'.repeat(Math.round(line.progress / 10))}${'░'.repeat(10 - Math.round(line.progress / 10))}] ${line.progress}%`}
              </>
            ) : ''}
          </p>
        ))}
        {!cleared && canProceed && (
          <span
            style={{ marginTop: 28, display: 'inline-block', color: '#78aaff', fontFamily: 'Manolo Mono, monospace', fontSize: 18, cursor: 'pointer', textDecoration: 'underline', fontWeight: 700, letterSpacing: 1.2 }}
            onClick={handleEnter}
          >
            {content[lang].enterBtn}<span className="blinking-cursor"></span>
          </span>
        )}
      </div>
    </div>
  );
}